import { Button, Card, Carousel, Input, Space, Switch, Upload, message } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SaveOutlined,
  ReloadOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  getHealthScreenConfig,
  updateHealthScreenConfigMultipart,
} from "../../services/healthScreen.service";

import "../HomeScreen/HomeScreen.css";

const { Dragger } = Upload;

function createSlide() {
  return {
    id: crypto.randomUUID(),
    imageUrl: "",
    storyImageUrl: "",
    title: "",
    description: "",
    linkUrl: "",
    file: null,
    storyFile: null,
    localPreviewUrl: null,
    localStoryPreviewUrl: null,
    useStoryImage: false,
  };
}

function revokePreview(slide) {
  if (slide?.localPreviewUrl) {
    URL.revokeObjectURL(slide.localPreviewUrl);
  }
  if (slide?.localStoryPreviewUrl) {
    URL.revokeObjectURL(slide.localStoryPreviewUrl);
  }
}

export default function HealthScreenPage() {
  const [slides, setSlides] = useState([createSlide()]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewSlideIndex, setPreviewSlideIndex] = useState(0);
  const previewCarouselRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { carousel } = await getHealthScreenConfig();
      setSlides((prev) => {
        prev.forEach(revokePreview);
        const rows = (carousel || []).map((item) => ({
          id: crypto.randomUUID(),
          imageUrl: (item.imageUrl || item.url || "").trim(),
          storyImageUrl: (item.storyImageUrl || item.storyUrl || "").trim(),
          title: (item.title ?? "").trim(),
          description: (item.description ?? "").trim(),
          linkUrl: (item.linkUrl ?? item.href ?? "").trim(),
          file: null,
          storyFile: null,
          localPreviewUrl: null,
          localStoryPreviewUrl: null,
          useStoryImage: Boolean(item.storyImageUrl || item.storyUrl),
        }));
        return rows.length ? rows : [createSlide()];
      });
    } catch {
      message.error("Failed to load health screen settings");
      setSlides((prev) => {
        prev.forEach(revokePreview);
        return [createSlide()];
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const previewSlides = useMemo(
    () =>
      slides.filter((s) => (s.localPreviewUrl || s.imageUrl || "").trim()),
    [slides]
  );

  useEffect(() => {
    setPreviewSlideIndex((i) =>
      previewSlides.length ? Math.min(i, previewSlides.length - 1) : 0
    );
  }, [previewSlides.length]);

  const setSlide = (id, patch) => {
    setSlides((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...patch } : s))
    );
  };

  const assignFile = (slideId, file, target = "main") => {
    if (!file.type.startsWith("image/")) {
      message.error("Please choose an image file");
      return;
    }
    const maxMb = 5;
    if (file.size / 1024 / 1024 > maxMb) {
      message.error(`Image must be ${maxMb}MB or smaller`);
      return;
    }
    setSlides((prev) =>
      prev.map((s) => {
        if (s.id !== slideId) return s;
        if (target === "story") {
          if (s.localStoryPreviewUrl) {
            URL.revokeObjectURL(s.localStoryPreviewUrl);
          }
          return {
            ...s,
            storyFile: file,
            localStoryPreviewUrl: URL.createObjectURL(file),
          };
        }
        if (s.localPreviewUrl) {
          URL.revokeObjectURL(s.localPreviewUrl);
        }
        return {
          ...s,
          file,
          localPreviewUrl: URL.createObjectURL(file),
        };
      })
    );
  };

  const moveSlide = (index, dir) => {
    setSlides((prev) => {
      const j = index + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  };

  const addSlide = () => {
    setSlides((prev) => [...prev, createSlide()]);
  };

  const removeSlide = (id) => {
    setSlides((prev) => {
      const removed = prev.find((s) => s.id === id);
      if (removed) revokePreview(removed);
      const next = prev.filter((s) => s.id !== id);
      return next.length ? next : [createSlide()];
    });
  };

  const clearImage = (id) => {
    setSlides((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;
        if (s.localPreviewUrl) {
          URL.revokeObjectURL(s.localPreviewUrl);
        }
        return { ...s, file: null, localPreviewUrl: null, imageUrl: "" };
      })
    );
  };

  const clearStoryImage = (id) => {
    setSlides((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;
        if (s.localStoryPreviewUrl) {
          URL.revokeObjectURL(s.localStoryPreviewUrl);
        }
        return {
          ...s,
          storyFile: null,
          localStoryPreviewUrl: null,
          storyImageUrl: "",
        };
      })
    );
  };

  const handleSave = async () => {
    const filled = slides.filter((s) => s.file || s.imageUrl.trim());
    if (!filled.length) {
      message.warning("Add at least one carousel image");
      return;
    }

    const invalidSlide = filled.find(
      (s) =>
        s.useStoryImage &&
        !(s.storyFile || s.localStoryPreviewUrl || s.storyImageUrl.trim())
    );
    if (invalidSlide) {
      message.warning(
        "Turn off “Full story in app” or add a full-story image for each slide that has it on"
      );
      return;
    }

    const carousel = filled.map((s) => ({
      title: s.title.trim() || undefined,
      description: s.description.trim() || undefined,
      linkUrl: s.useStoryImage ? undefined : s.linkUrl.trim() || undefined,
      imageUrl: s.file ? null : s.imageUrl.trim(),
      storyImageUrl: s.useStoryImage
        ? s.storyFile
          ? null
          : s.storyImageUrl.trim() || undefined
        : undefined,
      useStoryImage: s.useStoryImage || undefined,
    }));

    const files = filled.flatMap((s) =>
      [s.file, s.useStoryImage ? s.storyFile : null].filter(Boolean)
    );

    setSaving(true);
    try {
      await updateHealthScreenConfigMultipart({ carousel, files });
      message.success("Health screen saved — the app will pick up changes shortly");
      await load();
    } catch (e) {
      message.error(
        typeof e === "string" ? e : e?.message || "Failed to save health screen"
      );
    } finally {
      setSaving(false);
    }
  };

  const displaySrc = (slide) =>
    slide.localPreviewUrl || slide.imageUrl || "";

  return (
    <div className="home-screen-page">
      <div className="home-screen-heading">Health Screen</div>
      <p className="home-screen-subtitle">
        Manage health-mode carousel banners here; images are uploaded when you
        save. Unchanged slides keep their existing URLs. At least one image is
        required. Title, description, external link, and full-story image are
        all optional. If you turn on “Full story in app”, add the second image
        or turn the toggle off before saving.
      </p>

      {previewSlides.length > 0 && (
        <Card className="home-screen-preview-card" loading={loading}>
          <div className="home-screen-preview-title">Preview</div>
          <p className="home-screen-preview-caption">
            Same shape as the app health carousel (square, auto-play).
          </p>
          <div className="home-screen-preview-wrap">
            <div className="home-screen-carousel-billboard">
              <Carousel
                ref={previewCarouselRef}
                className="home-screen-carousel-preview"
                autoplay
                autoplaySpeed={4500}
                pauseOnHover
                dots={false}
                effect="scrollx"
                afterChange={setPreviewSlideIndex}
              >
                {previewSlides.map((ps, index) => {
                  const url = ps.localPreviewUrl || ps.imageUrl;
                  return (
                    <div key={`${index}-${url}`} className="home-screen-carousel-slide">
                      <img src={url} alt="" referrerPolicy="no-referrer" />
                      {(ps.title || ps.description) && (
                        <div className="home-screen-carousel-slide-copy">
                          {ps.title ? (
                            <div className="home-screen-carousel-slide-title">
                              {ps.title}
                            </div>
                          ) : null}
                          {ps.description ? (
                            <div className="home-screen-carousel-slide-desc">
                              {ps.description}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </Carousel>
              <div
                className="home-screen-carousel-dots"
                role="tablist"
                aria-label="Carousel slides"
              >
                {previewSlides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={i === previewSlideIndex}
                    aria-label={`Go to slide ${i + 1}`}
                    className={
                      i === previewSlideIndex
                        ? "home-screen-carousel-dot home-screen-carousel-dot--active"
                        : "home-screen-carousel-dot"
                    }
                    onClick={() => previewCarouselRef.current?.goTo(i)}
                  />
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card
        className="home-screen-form-card"
        title="Carousel"
        loading={loading}
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={load}
              disabled={loading || saving}
            >
              Reload
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSave}
            >
              Save changes
            </Button>
          </Space>
        }
      >
        <div className="home-screen-slides">
          {slides.map((slide, index) => (
            <div key={slide.id} className="home-screen-slide-row">
              <div className="home-screen-slide-main">
                {displaySrc(slide) ? (
                  <div className="home-screen-dropzone-filled">
                    <div className="home-screen-dropzone-preview">
                      <img
                        src={displaySrc(slide)}
                        alt=""
                        referrerPolicy="no-referrer"
                      />
                      <div
                        className="home-screen-dropzone-replace-layer"
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const file = e.dataTransfer?.files?.[0];
                          if (file) assignFile(slide.id, file, "main");
                        }}
                      />
                    </div>
                    <div className="home-screen-dropzone-filled-bar">
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        disabled={saving}
                        beforeUpload={(file) => {
                          assignFile(slide.id, file, "main");
                          return false;
                        }}
                      >
                        <Button type="primary" size="small" ghost>
                          Replace image
                        </Button>
                      </Upload>
                      <Button
                        type="primary"
                        danger
                        size="small"
                        onClick={() => clearImage(slide.id)}
                      >
                        Remove image
                      </Button>
                    </div>
                    <div className="home-screen-dropzone-replace-hint">
                      Or drag a new image onto the preview to replace (saved on
                      Save)
                    </div>
                  </div>
                ) : (
                  <Dragger
                    className="home-screen-dragger"
                    accept="image/*"
                    multiple={false}
                    showUploadList={false}
                    disabled={saving}
                    beforeUpload={(file) => {
                      assignFile(slide.id, file, "main");
                      return false;
                    }}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined style={{ color: "#00c183" }} />
                    </p>
                    <p className="home-screen-dragger-title">
                      Drop image here, or click to browse
                    </p>
                    <p className="home-screen-dragger-hint">
                      PNG, JPG, WebP up to 5MB — uploads when you save
                    </p>
                  </Dragger>
                )}

                <div className="home-screen-slide-fields">
                  <div className="home-screen-field-wrap">
                    <span className="home-screen-field-label">Title (optional)</span>
                    <Input
                      className="home-screen-field-input"
                      placeholder="e.g. Chicken rice bowl"
                      value={slide.title}
                      onChange={(e) =>
                        setSlide(slide.id, { title: e.target.value })
                      }
                      allowClear
                    />
                  </div>
                  <div className="home-screen-field-wrap">
                    <span className="home-screen-field-label">
                      Description (optional)
                    </span>
                    <Input.TextArea
                      className="home-screen-field-input home-screen-field-textarea"
                      placeholder="Short supporting copy for this slide"
                      value={slide.description}
                      onChange={(e) =>
                        setSlide(slide.id, { description: e.target.value })
                      }
                      allowClear
                      autoSize={{ minRows: 3, maxRows: 6 }}
                    />
                  </div>
                  <div className="home-screen-field-wrap">
                    <span className="home-screen-field-label">Action type</span>
                    <div className="home-screen-toggle-row">
                      <span className="home-screen-toggle-label">
                        Full story in app (use second image)
                      </span>
                      <Switch
                        checked={slide.useStoryImage}
                        onChange={(checked) =>
                          setSlide(slide.id, {
                            useStoryImage: checked,
                            linkUrl: checked ? "" : slide.linkUrl,
                          })
                        }
                        disabled={saving}
                      />
                    </div>
                  </div>
                  {slide.useStoryImage ? (
                    <div className="home-screen-field-wrap">
                      <span className="home-screen-field-label">
                        Full story image
                      </span>
                      <div className="home-screen-story-image-row">
                        {(slide.localStoryPreviewUrl || slide.storyImageUrl) && (
                          <img
                            src={slide.localStoryPreviewUrl || slide.storyImageUrl}
                            alt=""
                            referrerPolicy="no-referrer"
                            className="home-screen-story-image-preview"
                          />
                        )}
                        <Space wrap>
                          <Upload
                            accept="image/*"
                            showUploadList={false}
                            disabled={saving}
                            beforeUpload={(file) => {
                              assignFile(slide.id, file, "story");
                              return false;
                            }}
                          >
                            <Button size="small" type="default">
                              {(slide.localStoryPreviewUrl || slide.storyImageUrl)
                                ? "Replace full story image"
                                : "Upload full story image"}
                            </Button>
                          </Upload>
                          {(slide.localStoryPreviewUrl || slide.storyImageUrl) && (
                            <Button
                              size="small"
                              danger
                              onClick={() => clearStoryImage(slide.id)}
                            >
                              Remove
                            </Button>
                          )}
                        </Space>
                      </div>
                    </div>
                  ) : (
                    <div className="home-screen-field-wrap">
                      <span className="home-screen-field-label">
                        External link
                      </span>
                      <Input
                        className="home-screen-field-input"
                        placeholder="https://…"
                        value={slide.linkUrl}
                        onChange={(e) =>
                          setSlide(slide.id, { linkUrl: e.target.value })
                        }
                        allowClear
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="home-screen-slide-toolbar">
                <Button
                  type="text"
                  icon={<ArrowUpOutlined />}
                  disabled={index === 0}
                  onClick={() => moveSlide(index, -1)}
                  aria-label="Move up"
                />
                <Button
                  type="text"
                  icon={<ArrowDownOutlined />}
                  disabled={index === slides.length - 1}
                  onClick={() => moveSlide(index, 1)}
                  aria-label="Move down"
                />
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeSlide(slide.id)}
                  aria-label="Remove slide"
                />
              </div>
            </div>
          ))}
        </div>

        <Button
          type="dashed"
          onClick={addSlide}
          block
          icon={<PlusOutlined />}
          className="home-screen-add-slide"
          disabled={saving}
        >
          Add another slide
        </Button>
      </Card>
    </div>
  );
}
